import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult, Element } from '@/utils/sajuCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';
import { getWeightedElementDist } from '@/utils/singangyakCalculations';

interface Props {
  result: SajuResult;
}

const ELEMENT_EMOJIS: Record<Element, string> = {
  목: '🌲', 화: '🔥', 토: '🏔️', 금: '⚡', 수: '💧',
};

const ELEMENT_KEYS: Record<Element, string> = {
  목: 'wood', 화: 'fire', 토: 'earth', 금: 'metal', 수: 'water',
};

function BarChart({ element, value, max, animating, elementKey, elementName }: { element: Element; value: number; max: number; animating: boolean; elementKey: string; elementName: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!animating) return;
    const t = setTimeout(() => setWidth(pct), 100);
    return () => clearTimeout(t);
  }, [animating, pct]);

  return (
    <div className="flex items-center gap-3 mb-2.5">
      <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-none" style={{ backgroundColor: ELEMENT_BG_COLORS[element] }}>
        <span className="text-base">{ELEMENT_EMOJIS[element]}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-700">{elementName}</span>
          <span className="text-xs font-bold" style={{ color: ELEMENT_COLORS[element] }}>{Math.round(pct)}%</span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${width}%`, backgroundColor: ELEMENT_COLORS[element] }}
          />
        </div>
      </div>
    </div>
  );
}

export default function OhaengSection({ result }: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const weighted = getWeightedElementDist(result);
  const total = Object.values(weighted).reduce((a, b) => a + b, 0);

  const elements: Element[] = ['목', '화', '토', '금', '수'];
  const sorted = [...elements].sort((a, b) => weighted[b] - weighted[a]);
  const dominant = sorted[0];
  const lacking = sorted[sorted.length - 1];

  const pcts = elements.reduce((acc, el) => {
    acc[el] = total > 0 ? (weighted[el] / total) * 100 : 20;
    return acc;
  }, {} as Record<Element, number>);

  const maxPct = Math.max(...Object.values(pcts));

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="bg-white border border-gray-100 rounded-2xl p-5 mb-6 transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
    >
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
          <i className="ri-bar-chart-grouped-line text-amber-500 text-sm" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{t('ohaeng.title')}</h3>
          <p className="text-xs text-gray-400">{t('ohaeng.subtitle')}</p>
        </div>
      </div>

      {/* 지배 오행 강조 배너 */}
      <div
        className="rounded-2xl p-4 mb-5 flex items-center gap-4"
        style={{ backgroundColor: ELEMENT_BG_COLORS[dominant] }}
      >
        <div className="text-4xl flex-none">{ELEMENT_EMOJIS[dominant]}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: ELEMENT_COLORS[dominant] }}>
              {Math.round(pcts[dominant])}%
            </span>
            <span className="text-xs text-gray-500">{t('ohaeng.dominant')}</span>
          </div>
          <p className="text-base font-bold" style={{ color: ELEMENT_COLORS[dominant] }}>
            {t(`ohaeng.${ELEMENT_KEYS[dominant]}`)}{t('ohaeng.dominant.full')}
          </p>
          <p className="text-xs text-gray-600 leading-relaxed mt-1">
            {t(`ohaeng.personality.${ELEMENT_KEYS[dominant]}`)}
          </p>
        </div>
      </div>

      {/* 바 차트 */}
      <div className="mb-5">
        {sorted.map(el => (
          <BarChart key={el} element={el} value={pcts[el]} max={maxPct} animating={visible} elementKey={ELEMENT_KEYS[el]} elementName={t(`ohaeng.${ELEMENT_KEYS[el]}`)} />
        ))}
      </div>

      {/* 부족한 기운 */}
      <div className="bg-gray-50 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center rounded-lg" style={{ backgroundColor: ELEMENT_BG_COLORS[lacking] }}>
            <span className="text-xs">{ELEMENT_EMOJIS[lacking]}</span>
          </div>
          <p className="text-xs font-bold text-gray-700">{t('ohaeng.lacking')}</p>
        </div>
        <p className="text-sm font-bold mb-1" style={{ color: ELEMENT_COLORS[lacking] }}>
          {t(`ohaeng.${ELEMENT_KEYS[lacking]}`)} ({Math.round(pcts[lacking])}%)
        </p>
        <p className="text-xs text-gray-500 leading-relaxed">{t(`ohaeng.lacking.${ELEMENT_KEYS[lacking]}`)}</p>
      </div>

      {/* 오행 상생상극 미니 설명 */}
      <div className="mt-4 bg-amber-50 rounded-xl p-3">
        <p className="text-xs text-amber-700 leading-relaxed">
          <strong>{t('ohaeng.flow.label')}</strong> {t('ohaeng.flow.desc')}
        </p>
      </div>
    </div>
  );
}
